import { Divider, Form, Switch } from 'antd';
import {
  Button,
  InformationBox,
  Input,
  Select,
  useTheme,
} from '@prio365/prio365-react-library';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CreateProjectExtension,
  ProjectExtension,
  UpdateProjectExtension,
} from '../../../../../../models/ProjectExtension';
import { ProjectExtensionId, ProjectId } from '../../../../../../models/Types';
import { colon } from '../../../../../../util/forms';
import { apiFetchProjectFileStructure } from '../../../../../settings/api';
import { ProjectFileSystemStructure } from '../../../../../../models/ProjectFileStructure';
import { useSelector } from 'react-redux';
import {
  RootReducerState,
  getUserMe,
} from '../../../../../../apps/main/rootReducer';
import { User } from '../../../../../../models/User';
import Flex from '../../../../../../components/Flex';
import { makePrioStyles } from '../../../../../../theme/utils';
import { PrioTheme } from '../../../../../../theme/types';
import SharepointProjectExtensionModal from './SharepointProjectExtensionFormModal';

const useStyles = makePrioStyles((theme) => ({
  root: {
    width: '100%',
    minHeight: '100px',
  },
  label: {
    color: theme.colors.application.typography.muted,
  },
  submitButton: {
    position: 'absolute',
    bottom: '32px',
  },
}));

export interface SharepointProjectExtensionFormModel {
  displayName: string;
  projectFileSystemStructureId: string;
  hasDefaultAccess: boolean;
  IsTeamEnabled?: boolean;
}

export type SharepointProjectExtensionFormModelType =
  | 'createFolderStructureModal'
  | 'updateFolderStructureModal';

interface SharepointProjectExtensionFormProps {
  projectExtension: ProjectExtension;
  addProjectExtension: CreateProjectExtension;
  projectId: ProjectId;
  updateProjectExtension: (
    projectId: ProjectId,
    projectExtensionId: ProjectExtensionId,
    projectExtension: UpdateProjectExtension
  ) => Promise<ProjectExtension>;
  createProjectExtension: (
    projectId: ProjectId,
    createProjectExtension: CreateProjectExtension
  ) => Promise<ProjectExtension>;
  reloadProjectExtension: () => void;
}

export const SharepointProjectExtensionForm: React.FC<
  SharepointProjectExtensionFormProps
> = (props) => {
  //#region ------------------------------ Defaults
  const {
    projectExtension,
    addProjectExtension,
    projectId,
    updateProjectExtension,
    createProjectExtension,
    reloadProjectExtension,
  } = props;
  const classes = useStyles();
  const [form] = Form.useForm();
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const userMe = useSelector<RootReducerState, User>(getUserMe);
  const userIsGlobalAdmin =
    userMe?.prioData.globalRoles.includes('globalAdmin') ?? false;
  const [loading, setLoading] = useState<boolean>(false);
  const [initialValue, setInitialValue] =
    useState<SharepointProjectExtensionFormModel>(
      projectExtension
        ? {
            displayName: projectExtension.displayName,
            projectFileSystemStructureId: projectExtension.options?.find(
              (option) => option.key === 'projectFileSystemStructureId'
            )?.value,
            hasDefaultAccess: projectExtension.hasDefaultAccess,
            IsTeamEnabled: projectExtension.options?.find(
              (option) => option.key === 'teams'
            )
              ? JSON.parse(
                  projectExtension.options?.find(
                    (option) => option.key === 'teams'
                  )?.value
                ).IsTeamEnabled
              : false,
          }
        : {
            displayName: t('projects:documents'),
            projectFileSystemStructureId: '',
            hasDefaultAccess: true,
            IsTeamEnabled: true,
          }
    );
  const [projectFileStructure, setProjectFileStructure] = useState<
    ProjectFileSystemStructure[]
  >([]);
  const [modalType, setModalType] = useState<
    SharepointProjectExtensionFormModelType | undefined
  >(undefined);
  const [newProjectFolderStructureId, setNewProjectFolderStructureId] =
    useState<string | undefined>(undefined);
  const [inputValue, setInputValue] = useState<string>(undefined);
  const [toggleValue, setToggleValue] = useState<boolean>(false);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const saveOrUpdateExtension = async (
    sharepointProjectExtensionFormModel: SharepointProjectExtensionFormModel
  ) => {
    setLoading(true);
    if (projectExtension && !addProjectExtension) {
      await updateProjectExtension(
        projectId,
        projectExtension.projectExtensionId,
        {
          displayName: sharepointProjectExtensionFormModel.displayName,

          hasDefaultAccess:
            sharepointProjectExtensionFormModel?.hasDefaultAccess,
        }
      );
    } else if (addProjectExtension && !projectExtension) {
      await createProjectExtension(projectId, {
        displayName: sharepointProjectExtensionFormModel.displayName,
        projectExtensionType: 'sharepoint',
        hasDefaultAccess: true,
        addProjectExtensionOptions: [
          {
            key: 'projectFileSystemStructureId',
            value:
              sharepointProjectExtensionFormModel.projectFileSystemStructureId,
          },
          {
            key: 'teams',
            value: JSON.stringify({
              IsTeamEnabled: sharepointProjectExtensionFormModel.IsTeamEnabled,
              AllowCreatePrivateChannels: true,
              AllowCreateUpdateChannels: true,
              AllowUserEditMessages: true,
              AllowUserDeleteMessages: true,
              AllowGiphy: true,
              GiphyContentRating: 'strict',
            }),
          },
        ],
      });
    }
    setLoading(false);
  };

  const closeModal = () => {
    setModalType(undefined);
    setNewProjectFolderStructureId(undefined);
    setInputValue(undefined);
    setToggleValue(false);
  };

  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (projectExtension) {
      setInitialValue({
        displayName: projectExtension.displayName,
        projectFileSystemStructureId: projectExtension.options?.find(
          (option) => option.key === 'projectFileSystemStructureId'
        )?.value,
        hasDefaultAccess: projectExtension.hasDefaultAccess,
      });
    }
    form.resetFields();
  }, [projectExtension, form]);

  useEffect(() => {
    const loadProjectFileStructure = async () => {
      const { data } = await apiFetchProjectFileStructure();

      if (data) {
        setProjectFileStructure(data);
        if (data?.length > 0) {
          form.setFieldsValue({
            projectFileSystemStructureId:
              data[0]['projectFileSystemStructureId'],
          });
        }
      }
    };
    loadProjectFileStructure();
  }, [form]);
  //#endregion

  return (
    <>
      {projectExtension != null ? (
        <>
          <p>
            {t('projects:addIn.sharepoint')}: {initialValue.displayName}
          </p>
          <p>
            {`${t('projects:form.labels.fileStructure')}: ${
              projectFileStructure.find(
                (fileStructure) =>
                  fileStructure.projectFileSystemStructureId ===
                  initialValue.projectFileSystemStructureId
              )?.name
            }`}
          </p>
          {userIsGlobalAdmin && (
            <Flex.Column childrenGap={theme.spacing.small}>
              <Divider />
              <h2>{t('projects:adminSetting.title')}</h2>
              <p className={classes.label}>
                {t('projects:adminSetting.createFolderStructureModalDesc')}
              </p>
              <Flex.Row childrenGap={theme.spacing.small}>
                <Button
                  type="default"
                  onClick={() => setModalType('createFolderStructureModal')}
                >
                  {t(
                    'projects:adminSetting.buttons.createFolderStructureModal'
                  )}
                </Button>
                <Button
                  type="default"
                  onClick={() => setModalType('updateFolderStructureModal')}
                >
                  {t(
                    'projects:adminSetting.buttons.updateFolderStructureModal'
                  )}
                </Button>
              </Flex.Row>
            </Flex.Column>
          )}
        </>
      ) : (
        <>
          <Form
            form={form}
            layout="vertical"
            className={classes.root}
            initialValues={initialValue}
            onFinish={(values) => {
              saveOrUpdateExtension(values);
            }}
          >
            <Form.Item
              name="displayName"
              required={true}
              colon={colon}
              rules={[
                {
                  required: true,
                  message: t('projects:form.validation.displayName'),
                },
              ]}
              label={t('projects:addIn.extensions.sharepoint.form.displayName')}
            >
              <Input full disabled={loading} />
            </Form.Item>
            <InformationBox
              description={t(
                'projects:addIn.extensions.sharepoint.displayNameDescription'
              )}
            />
            <Form.Item
              name="projectFileSystemStructureId"
              label={t('projects:form.labels.fileStructure')}
              validateTrigger="onBlur"
              rules={[
                {
                  required: true,
                  message: t('projects:form.validation.missingFileStructure'),
                },
              ]}
            >
              <Select
                options={projectFileStructure.map((fileStructure) => {
                  return {
                    value: fileStructure.projectFileSystemStructureId,
                    key: fileStructure.projectFileSystemStructureId,
                    label: fileStructure.name,
                  };
                })}
              />
            </Form.Item>
            <InformationBox
              description={t(
                'projects:addIn.extensions.sharepoint.folderStructureDescription'
              )}
            />
            <Form.Item
              name="IsTeamEnabled"
              colon={colon}
              validateTrigger="onBlur"
              label={t(
                'projects:addIn.extensions.sharepoint.form.isTeamEnabled'
              )}
              valuePropName="checked"
            >
              <Switch disabled={loading} />
            </Form.Item>
            <InformationBox
              description={t(
                'projects:addIn.extensions.sharepoint.isTeamEnabledDescription'
              )}
            />
            <Form.Item
              name="hasDefaultAccess"
              colon={colon}
              required={true}
              label={t('projects:addIn.extensions.form.hasDefaultAccess')}
              rules={[
                {
                  required: true,
                  message: t(
                    'projects:form.validation.hasDefaultAccessRequired'
                  ),
                },
              ]}
              valuePropName="checked"
            >
              <Switch disabled={loading} />
            </Form.Item>
            <InformationBox
              description={t(
                'projects:addIn.extensions.sharepoint.hasDefaultAccessDescription'
              )}
            />
            <Flex.Row flex={1} justifyContent="flex-end">
              <Button
                htmlType="submit"
                disabled={loading}
                loading={loading}
                className={classes.submitButton}
              >
                {t('projects:projectCreation.save')}
              </Button>
            </Flex.Row>
          </Form>
        </>
      )}
      <SharepointProjectExtensionModal
        projectFileStructure={projectFileStructure}
        closeModal={closeModal}
        reloadProjectExtension={reloadProjectExtension}
        inputValue={inputValue}
        setInputValue={setInputValue}
        setNewProjectFolderStructureId={setNewProjectFolderStructureId}
        modalType={modalType}
        newProjectFolderStructureId={newProjectFolderStructureId}
        toggleValue={toggleValue}
        setToggleValue={setToggleValue}
        initialValue={initialValue}
        projectId={projectId}
        projectExtension={projectExtension}
      />
    </>
  );
};

export default SharepointProjectExtensionForm;
